import { Modal, Select, InputNumber, Button, Spin, message } from "antd";
import { DeleteOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { svg } from "../../../../../Assets/svg";
import { useAppDispatch } from "../../../../../store";
import { getPossibleDimesionsAPI } from "../../../Quotations/quotationApis";
import {
  ProductDropEnum,
  ProductDropLabels,
} from "../../../Products/productsTypes";
import { resolveTBCVariantsAPI } from "../../orderApis";
import { GetOrderBomAndWarningsAPI } from "../../../../Redux/orders";
import { decryptByText } from "../../../../../helpers/bycrypt";
import { CapitalizeFirstWord } from "../../../../../helpers/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  orderId: string;
  editData?: any;
  quoteId?: string;
}

const ConfigureDropTBCVariantModal: React.FC<Props> = ({
  open,
  onClose,
  orderId,
  editData,
  quoteId,
}) => {
  const dispatch = useAppDispatch();
  const totalQty = editData?.variant?.quantity || 0;

  const [allocations, setAllocations] = useState<any[]>([]);
  const [dropOptions, setDropOptions] = useState<any[]>([]);
  const [customDropOptions, setCustomDropOptions] = useState<any[]>([]);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && editData) {
      setAllocations([
        { id: uuidv4(), drop: null, custom_drop: null, qty: totalQty },
      ]);
      setIsFetchingOptions(true);

      const activeFilters: any = {};
      if (editData.variant?.attributes) {
        Object.keys(editData.variant.attributes).forEach((key) => {
          if (
            editData.variant.attributes[key] !== null &&
            key !== "drop" &&
            key !== "custom_drop"
          ) {
            activeFilters[key] = editData.variant.attributes[key];
          }
        });
      }

      dispatch(
        getPossibleDimesionsAPI({
          filters: {
            product_id: editData.parent?.product_id || editData.parent?._id,
            category_id: editData.parent?.category_id,
            ...activeFilters,
          },
          onSuccess: (data: any) => {
            const validDrops = (data?.possible_values?.drop || []).filter(
              (d: string) => d !== "drop_tbc",
            );
            setDropOptions(validDrops);
            setCustomDropOptions(data?.possible_values?.custom_drop || []);
            setIsFetchingOptions(false);
          },
          onError: () => setIsFetchingOptions(false),
        }),
      );
    } else {
      setAllocations([]);
      setDropOptions([]);
    }
  }, [open, editData, totalQty]);

  const allocatedQty = allocations.reduce(
    (sum, item) => sum + (item.qty || 0),
    0,
  );
  const remainingQty = totalQty - allocatedQty;

  const handleUpdateRow = (id: string, key: string, value: any) => {
    setAllocations(
      allocations.map((row) =>
        row.id === id ? { ...row, [key]: value } : row,
      ),
    );
  };

  const handleAddRow = () => {
    setAllocations([
      ...allocations,
      {
        id: uuidv4(),
        drop: null,
        custom_drop: null,
        qty: remainingQty > 0 ? remainingQty : 1,
      },
    ]);
  };

  const handleRemoveRow = (id: string) => {
    setAllocations(allocations.filter((row) => row.id !== id));
  };

  const handleSplitRow = (rowId: string) => {
    const rowIndex = allocations.findIndex((r) => r.id === rowId);
    if (rowIndex === -1) return;

    const row = allocations[rowIndex];
    if (row.qty <= 1) return;

    const splitQty1 = Math.floor(row.qty / 2);
    const splitQty2 = row.qty - splitQty1;

    const updatedRow = { ...row, qty: splitQty1 };
    const newRow = { ...row, id: uuidv4(), qty: splitQty2 };

    const newAllocations = [...allocations];
    newAllocations[rowIndex] = updatedRow;
    newAllocations.splice(rowIndex + 1, 0, newRow);

    setAllocations(newAllocations);
  };

  const handleSave = () => {
    if (remainingQty !== 0) {
      message.error(`Please allocate all ${totalQty} items before saving.`);
      return;
    }

    const hasInvalidRows = allocations.some(
      (row) =>
        !row.drop ||
        (row.drop === "custom" &&
          (row.custom_drop === null ||
            row.custom_drop === undefined ||
            row.custom_drop === "")),
    );
    if (hasInvalidRows) {
      message.error("Please complete all fields for each configuration.");
      return;
    }

    setIsSaving(true);

    const replacements = allocations.map((row) => {
      const payloadItem: any = {
        drop: row.drop,
        quantity: row.qty,
      };
      if (
        row.drop === "custom" &&
        row.custom_drop !== null &&
        row.custom_drop !== undefined &&
        row.custom_drop !== ""
      ) {
        payloadItem.custom_drop = row.custom_drop;
      }
      return payloadItem;
    });

    const payload = {
      product_id: editData.parent?.product_id || editData.parent?._id,
      original_variant_id:
        editData.variant?.variant_id || editData.variant?._id,
      replacements: replacements,
    };

    dispatch(
      resolveTBCVariantsAPI({
        order_id: orderId,
        payload: payload,
        onSuccess: (data: any) => {
          const newData = {
            ...data,
            order_created_by: data?.order_created_by
              ? {
                  ...data?.order_created_by,
                  email: decryptByText(data?.order_created_by?.email),
                  first_name: CapitalizeFirstWord(
                    decryptByText(data?.order_created_by?.first_name),
                  ),
                  last_name: CapitalizeFirstWord(
                    decryptByText(data?.order_created_by?.last_name),
                  ),
                }
              : data?.created_by,
          };
          dispatch({
            type: "GET_ORDERS_DETAILS",
            payload: newData,
          });
          dispatch(GetOrderBomAndWarningsAPI({ order_id: orderId }));
          setIsSaving(false);
          onClose();
        },
        onError: () => {
          setIsSaving(false);
        },
      }),
    );
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={svg?.closeIcon}
      width={"70rem"}
      className="commonModalClass"
      title={
        <div className="text-center fs-4 fs-4-mobile  fw-bolder lh-m mb-4">
          Configure Drop TBC Variant
        </div>
      }
      rootClassName=" "
    >
      <div className="p-2">
        <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-4">
          <div>
            <h6 className="fw-bold mb-1 fs-6 fs-6-mob">{editData?.variant?.variant_name}</h6>
            <small className="text-muted">
              Total items to configure:{" "}
              <span className="fw-bold text-dark">{totalQty}</span>
            </small>
          </div>
          <div
            className={`badge ${
              remainingQty === 0 ? "bg-success" : "bg-warning text-dark"
            } p-2 px-3 rounded-pill fs-6`}
          >
            Remaining: {remainingQty}
          </div>
        </div>

        {isFetchingOptions ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {allocations.map((row, index) => (
              <div className="card bg-light rounded border mx-0 position-relative">
                  <div className="card-body p-3">
                      <div
                        key={row.id}
                        className="row gy-md-3 gy-2 page-form align-items-end"
                      >
                        <div className="col-md-3 col-sm-6">
                          <label className="small fw-bold mb-1">Drop</label>
                          <Select
                            className="w-100 auto-height-select"
                            popupClassName="full-text-select-dropdown"
                            placeholder="Select Drop"
                            showSearch
                            suffixIcon={svg?.selectDownArrow}
                            value={row.drop}
                            onChange={(val) => handleUpdateRow(row.id, "drop", val)}
                            options={dropOptions.map((opt) => ({
                              label: ProductDropLabels[opt as ProductDropEnum],
                              value: opt,
                            }))}
                          />
                        </div>

                        {row.drop === "custom" && (
                          <div className="col-md-3 col-sm-6">
                            <label className="small fw-bold mb-1">
                              Custom Drop (mm)
                            </label>
                            <Select
                              className="w-100 auto-height-select"
                              popupClassName="full-text-select-dropdown"
                              placeholder="Select"
                              showSearch
                              suffixIcon={svg?.selectDownArrow}
                              value={row.custom_drop}
                              onChange={(val) =>
                                handleUpdateRow(row.id, "custom_drop", val)
                              }
                              options={customDropOptions.map((opt) => ({
                                label: String(opt),
                                value: opt,
                              }))}
                            />
                          </div>
                        )}

                        <div className="col-md-3 col-sm-6 ms-md-auto">
                          <label className="small fw-bold mb-1">Qty</label>
                          <div className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput">
                            <Button
                              type="text"
                              icon={<MinusOutlined />}
                              className="btn btn-primary addRemoveBtn"
                              onClick={() =>
                                handleUpdateRow(row.id, "qty", Math.max(1, row.qty - 1))
                              }
                              disabled={row.qty <= 1}
                            />
                            <InputNumber
                              value={row.qty}
                              min={1}
                              max={totalQty}
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(val) =>
                                handleUpdateRow(row.id, "qty", val || 1)
                              }
                              className="border-0 text-center w-100 shadow-none"
                              controls={false}
                            />
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              className="btn btn-primary addRemoveBtn"
                              onClick={() =>
                                handleUpdateRow(
                                  row.id,
                                  "qty",
                                  Math.min(totalQty, row.qty + 1),
                                )
                              }
                              disabled={row.qty >= totalQty || remainingQty <= 0}
                            />
                          </div>
                        </div>

                        <div
                          className="col-md-auto position-absolute top-0 end-0 mt-2 me-2 d-flex gap-2"
                          style={{ width: "auto" }}
                        >
                          {row.qty > 1 && (
                            <Button
                              type="text"
                              size="small"
                              className="text-primary fw-bold"
                              onClick={() => handleSplitRow(row.id)}
                            >
                              Split
                            </Button>
                          )}
                          {allocations.length > 1 && (
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveRow(row.id)}
                            />
                          )}
                        </div>
                      </div>
                  </div>
              </div>
              
            ))}

            <div className="row gy-3 justify-content-sm-between justify-content-center align-items-center mt-3">
              <div className="col-sm-5 col-12 text-sm-start text-center">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddRow}
                disabled={remainingQty <= 0}
              >
                Add Another Configuration
              </Button>
              </div>
              <div className="col-sm-auto col-12">
              <Button
                type="primary"
                size="large"
                onClick={handleSave}
                loading={isSaving}
                className="w-100"
                disabled={
                  remainingQty !== 0 ||
                  allocations.some(
                    (row) =>
                      !row.drop ||
                      (row.drop === "custom" &&
                        (row.custom_drop === null ||
                          row.custom_drop === undefined ||
                          row.custom_drop === "")),
                  )
                }
              >
                SAVE DETAILS
              </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConfigureDropTBCVariantModal;
